import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore'
import DashboardRepository, { StreamFilter, StreamMessageType } from '../repositories/dashboard-repository'
import { RootState } from '@/store'
import { EVENT_TYPES } from './utils'

export default class DashboardService {
    constructor(private dashboardRepository: DashboardRepository, private store: ToolkitStore<RootState>) {}

    getStreams(
        filter: Partial<StreamFilter> = {
            Page: 1,
            PageSize: 100,
        },
    ) {
        return this.dashboardRepository.getStreams(filter)
    }

    getServiceRequestEventDetails({ accountNumber, eventName }: { accountNumber: string; eventName: string }) {
        return this.dashboardRepository.getServiceRequestEventDetails({ accountNumber, eventName })
    }

    getStreamLogo = (messageType: string, status?: string | null): string => {
        messageType = messageType?.toLowerCase()
        const statusKey = (status ?? '').toLowerCase()

        const IMAGE_MAP = {
            [StreamMessageType.COMPLAINT]: {
                default: '/images/icons/report.svg',
            },
            [StreamMessageType.FRAUD_BILL_NOTIFICATION]: {
                default: '/images/icons/report.svg',
            },
            [StreamMessageType.ISSUE_NOTIFICATION]: {
                default: '/images/icons/report.svg',
            },
            [StreamMessageType.INVOICE]: {
                default: '/images/icons/cash.svg',
            },
            [StreamMessageType.PAYMENT_NOTIFICATION]: {
                default: '/images/icons/cash.svg',
            },
            [StreamMessageType.NOTIFICATION]: {
                default: '/images/icons/notify.svg',
            },
            [StreamMessageType.BILLING_TYPE_CHANGE]: {
                default: '/images/icons/billing-type-change.svg',
            },
            [StreamMessageType.PAY_SMALL_SMALL]: {
                default: '/images/icons/pay-small-small.svg',
            },
            [StreamMessageType.PAY_SMALL_SMALL_DEBIT_FAILURE]: {
                default: '/images/icons/pay-small-small-debit-failure.svg',
            },
            [StreamMessageType.CHANGE_ACCOUNT_DETAILS]: {
                default: '/images/icons/service-request/request_change_of_account.png',
            },
            [StreamMessageType.STATEMENT_REQUEST]: {
                default: '/images/icons/service-request/request_customer_statement.png',
                refunded: '/images/icons/service-request/request_customer_statement_completed.png',
                completed: '/images/icons/service-request/request_customer_statement_completed.png',
            },
            [StreamMessageType.TEMPORARY_DISCONNECTION_REQUEST]: {
                default: '/images/icons/service-request/temp_disconnection.png',
            },
            [StreamMessageType.RECONNECTION_REQUEST]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.METER_TRANSFER_REQUEST]: {
                default: '/images/icons/service-request/meter_transfer.png',
                complete: '/images/icons/service-request/meter_transfer_complete.png',
            },
            [StreamMessageType.DELAYED_INSTALLATION]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.STOLEN_METER]: {
                default: '/images/icons/service-request/stolen_meter.png',
            },
            [StreamMessageType.REPORT_ISSUE_WITH_POSTPAID_BILL]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.FAULTY_METER]: {
                default: '/images/icons/service-request/faulty_meter.png',
            },
            [StreamMessageType.FAULTY_METER_2]: {
                default: '/images/icons/service-request/faulty_meter.png',
            },
            [StreamMessageType.ILLEGAL_CONNECTION]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.ILLEGAL_CONNECTION_2]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.TASK_PROGRESS]: {
                default: '/images/icons/notify.svg',
            },
            [StreamMessageType.REQUEST_PROGRESS]: {
                default: '/images/icons/notify.svg',
            },
            [StreamMessageType.TASK_ASSIGNMENT_NOTICE]: {
                default: '/images/icons/notify.svg',
            },
            [StreamMessageType.METER_NOT_FOUND]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.METER_NOT_FOUND_2]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.METER_NOT_FOUND_3]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.NEW_SERVICE_REQUEST]: {
                default: '/images/icons/new-service-application-received.svg',
            },
            [StreamMessageType.POSTPAID_BILL]: {
                default: '/images/icons/postpaid-cutoff-notification.svg',
            },
            [StreamMessageType.STATEMENT_REQUEST_FAILED]: {
                default: '/images/icons/user.svg',
            },
            [StreamMessageType.METER_REPLACED]: {
                default: '/images/icons/postpaid-cutoff-notification.svg',
            },
            [EVENT_TYPES.newServiceApplicationReceived.type.toLowerCase()]: {
                default: '/images/icons/new-service-application-received.svg',
            },
            [EVENT_TYPES.ApplicationFeeGenerated.type.toLowerCase()]: {
                default: '/images/icons/cash.svg',
            },
            [EVENT_TYPES.ConnectionFeeGenerated.type.toLowerCase()]: {
                default: '/images/icons/connection-fee-generated.svg',
            },
            [EVENT_TYPES.ConnectionFeePaid.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.ApplicationFeeGenerated.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.ApplicationFeePaid.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.InspectionInitiated.type.toLowerCase()]: {
                default: '/images/icons/connection-fee-generated.svg',
            },
            [EVENT_TYPES.InspectionFailed.type.toLowerCase()]: {
                default: '/images/icons/inspection-failed.svg',
            },
            [EVENT_TYPES.InspectionSuccessful.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.ContractSigned.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.ContractGenerated.type.toLowerCase()]: {
                default: '/images/icons/connection-fee-generated.svg',
            },
            [EVENT_TYPES.InstallationInitiated.type.toLowerCase()]: {
                default: '/images/icons/connection-fee-generated.svg',
            },
            [EVENT_TYPES.InstallationAssigned.type.toLowerCase()]: {
                default: '/images/icons/installation-assigned.svg',
            },
            [EVENT_TYPES.InstallationSuccessful.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.InstallationFailed.type.toLowerCase()]: {
                default: '/images/icons/inspection-failed.svg',
            },
            [EVENT_TYPES.ServiceRequestCompleted.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.CustomerTariffClassUpdated.type.toLowerCase()]: {
                default: '/images/icons/application-fee-paid.svg',
            },
            [EVENT_TYPES.NewMeterInstalled.type.toLowerCase()]: {
                default: '/images/icons/new-meter-installed.svg',
            },
            [EVENT_TYPES.MeterReplaced.type.toLowerCase()]: {
                default: '/images/icons/meter-replaced.svg',
            },
            [EVENT_TYPES.CustomerAccountDetailChangeRejected.type.toLowerCase()]: {
                default: '/images/icons/details-change-rejected.svg',
            },
            [EVENT_TYPES.CustomerAccountDetailChangeApproved.type.toLowerCase()]: {
                default: '/images/icons/details-change-approved.svg',
            },
            [EVENT_TYPES.CustomerUpdated.type.toLowerCase()]: {
                default: '/images/icons/customer-details-updated.svg',
            },
            [EVENT_TYPES.MeterReading.type.toLowerCase()]: {
                default: '/images/icons/meter-replaced.svg',
            },
            [EVENT_TYPES.MeterReadingException.type.toLowerCase()]: {
                default: '/images/icons/new-meter-installed.svg',
            },
            [EVENT_TYPES.MeterReadingAnomaly.type.toLowerCase()]: {
                default: '/images/icons/meter-reading-anomaly.svg',
            },
            [StreamMessageType.METER_CARD_REPLACEMENT]: {
                default: '/images/icons/service-request/meter_card_replacement.png',
            },
        } as Record<string, Record<string, string>>

        if (IMAGE_MAP[messageType]) {
            return IMAGE_MAP[messageType][statusKey] || IMAGE_MAP[messageType].default
        }

        return '/images/icons/notify.svg' // Default icon if no match is found
    }

    isStreamEvent(messageType: string) {
        if (!messageType) return false
        const lowerType = messageType.toLowerCase()
        return Object.values(EVENT_TYPES).some(key => {
            return key.type.toLowerCase() === lowerType
        })
    }

    async downloadFileFromUrl(url: string) {
        return this.dashboardRepository.downloadFileFromUrl(url)
    }
}
